import type { EventObject, MachineContext } from 'xstate';

import type { MachineState } from '../../interfaces/machines';
import type { RequireOne } from '../../interfaces/RequireOne';

import { OnboardingAuthentication } from './components/OnboardingAuthentication/OnboardingAuthentication';
import { StandaloneAuthentication } from './components/StandaloneAuthentication/StandaloneAuthentication';
import { isValidAuthenticationFlow } from './helpers/flow/isValidAuthenticationFlow';

interface AuthenticationProps<C extends MachineContext, E extends EventObject> {
  initialFlow: string | null;
  onboardingState: MachineState<C, E>;
}

export const Authentication = <C extends MachineContext, E extends EventObject>({
  initialFlow,
  onboardingState,
}: RequireOne<AuthenticationProps<C, E>>) => {
  const doesOnboardingStateExist = !!onboardingState;
  const isAuthenticationServiceAvailable = !!onboardingState?.children.authenticationService;

  if (initialFlow && onboardingState) {
    throw new Error(
      'You can only pass either initialFlow or onboardingState for the Authentication component.',
    );
  }

  if (initialFlow && !isValidAuthenticationFlow(initialFlow)) {
    throw new Error('Given initial Flow is not a valid authentication flow.');
  }

  return (
    <>
      {initialFlow && isValidAuthenticationFlow(initialFlow) && (
        <StandaloneAuthentication initialFlow={initialFlow} />
      )}
      {doesOnboardingStateExist && isAuthenticationServiceAvailable && (
        <OnboardingAuthentication onboardingState={onboardingState} />
      )}
    </>
  );
};
